// apps/client/src/ui-kit/Toast.tsx
//
// Floating notification pill — replaces all alert() and inline error text.
// Auto-dismisses after 3 seconds. Three variants: success, error, info.
//

import { motion, AnimatePresence } from 'framer-motion';

export interface ToastData {
    show: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
}

interface ToastProps {
    toast: ToastData;
    onDismiss?: () => void;
}

const variantStyles = {
    success: 'bg-emerald-500 text-white',
    error: 'bg-red-500 text-white',
    info: 'bg-[#6D28D9] text-white',
};

const variantIcons = {
    success: '✅',
    error: '❌',
    info: 'ℹ️',
};

export function Toast({ toast, onDismiss }: ToastProps) {
    return (
        <AnimatePresence>
            {toast.show && (
                <motion.div
                    initial={{ y: 100, opacity: 0, scale: 0.9 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: 100, opacity: 0, scale: 0.9 }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    onClick={onDismiss}
                    className={`
                        fixed bottom-24 left-4 right-4 z-50
                        px-5 py-3 rounded-pill
                        flex items-center gap-3
                        font-bold text-sm
                        shadow-lg cursor-pointer
                        ${variantStyles[toast.type]}
                    `}
                >
                    <span className="text-lg">{variantIcons[toast.type]}</span>
                    <span className="flex-1">{toast.message}</span>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// Helper hook for toast state management
export function useToast() {
    const [toast, setToast] = useState<ToastData>({
        show: false,
        message: '',
        type: 'info',
    });

    const showToast = (message: string, type: ToastData['type'] = 'info', duration = 3000) => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast((t) => ({ ...t, show: false })), duration);
    };

    const dismissToast = () => setToast((t) => ({ ...t, show: false }));

    return { toast, showToast, dismissToast };
}

// Need useState import for the hook
import { useState } from 'react';
